<template>
  <div class="smart-captcha-field">
    <div ref="container" class="smart-captcha-field__widget"></div>
    <div
      v-if="message"
      class="smart-captcha-field__message"
      :class="{ 'smart-captcha-field__message--error': isError }"
      role="status"
    >
      {{ message }}
    </div>
    <button
      v-if="state === 'unavailable' && siteKey"
      type="button"
      class="smart-captcha-field__retry"
      @click="renderWidget"
    >
      Повторить проверку
    </button>
  </div>
</template>

<script>
const SCRIPT_ID = "yandex-smartcaptcha-script"
const SCRIPT_URL =
  "https://smartcaptcha.cloud.yandex.ru/captcha.js?render=onload"

let scriptPromise = null

function loadSmartCaptcha() {
  if (window.smartCaptcha) return Promise.resolve(window.smartCaptcha)
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise((resolve, reject) => {
    let script = document.getElementById(SCRIPT_ID)

    const handleLoad = () => {
      if (window.smartCaptcha) resolve(window.smartCaptcha)
      else reject(new Error("SmartCaptcha API is unavailable"))
    }
    const handleError = () => {
      if (script.parentNode) script.parentNode.removeChild(script)
      reject(new Error("SmartCaptcha failed to load"))
    }

    if (!script) {
      script = document.createElement("script")
      script.id = SCRIPT_ID
      script.src = SCRIPT_URL
      script.async = true
      script.defer = true
    }

    script.addEventListener("load", handleLoad, { once: true })
    script.addEventListener("error", handleError, { once: true })
    if (!script.parentNode) document.head.appendChild(script)
  }).catch((error) => {
    scriptPromise = null
    throw error
  })

  return scriptPromise
}

export default {
  name: "SmartCaptcha",
  props: {
    value: {
      type: String,
      default: "",
    },
    siteKey: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      widgetId: null,
      state: "loading",
      unsubscribers: [],
    }
  },
  computed: {
    isError() {
      return ["expired", "unavailable"].includes(this.state)
    },
    message() {
      if (this.state === "loading") return "Проверка загружается…"
      if (this.state === "expired")
        return "Срок проверки истёк. Подтвердите, что вы не робот ещё раз"
      if (this.state === "unavailable")
        return "Не удалось выполнить проверку. Повторите попытку позже"
      return ""
    },
  },
  watch: {
    siteKey(newValue, oldValue) {
      if (newValue && newValue !== oldValue) this.renderWidget()
    },
  },
  mounted() {
    this.renderWidget()
  },
  beforeDestroy() {
    this.destroyWidget()
  },
  methods: {
    emitState(state) {
      this.state = state
      this.$emit("state", state)
    },
    async renderWidget() {
      if (!this.siteKey) {
        this.emitState("unavailable")
        return
      }

      this.destroyWidget()
      this.emitState("loading")

      try {
        const smartCaptcha = await loadSmartCaptcha()
        if (this._isBeingDestroyed || this._isDestroyed || !this.$refs.container)
          return

        this.widgetId = smartCaptcha.render(this.$refs.container, {
          sitekey: this.siteKey,
          hl: "ru",
          callback: (token) => {
            this.$emit("input", token || "")
            this.emitState(token ? "verified" : "ready")
          },
        })

        this.subscribe("token-expired", () => {
          this.$emit("input", "")
          if (
            window.smartCaptcha &&
            typeof window.smartCaptcha.reset === "function"
          )
            window.smartCaptcha.reset(this.widgetId)
          this.emitState("expired")
        })
        this.subscribe("network-error", this.handleUnavailable)
        this.subscribe("javascript-error", this.handleUnavailable)
        this.emitState("ready")
      } catch (error) {
        this.handleUnavailable()
      }
    },
    subscribe(eventName, callback) {
      if (
        this.widgetId === null ||
        !window.smartCaptcha ||
        typeof window.smartCaptcha.subscribe !== "function"
      )
        return

      const unsubscribe = window.smartCaptcha.subscribe(
        this.widgetId,
        eventName,
        callback
      )
      if (typeof unsubscribe === "function") this.unsubscribers.push(unsubscribe)
    },
    handleUnavailable() {
      this.$emit("input", "")
      this.emitState("unavailable")
    },
    reset() {
      this.$emit("input", "")
      if (
        this.widgetId !== null &&
        window.smartCaptcha &&
        typeof window.smartCaptcha.reset === "function"
      ) {
        window.smartCaptcha.reset(this.widgetId)
        this.emitState("ready")
      } else {
        this.renderWidget()
      }
    },
    destroyWidget() {
      this.unsubscribers.forEach((unsubscribe) => unsubscribe())
      this.unsubscribers = []

      if (
        this.widgetId !== null &&
        window.smartCaptcha &&
        typeof window.smartCaptcha.destroy === "function"
      ) {
        window.smartCaptcha.destroy(this.widgetId)
      }
      this.widgetId = null
    },
  },
}
</script>
